import React, { useState, useContext, useMemo } from 'react';
import {
	Accordion,
	AccordionContext,
	Col,
	Row,
	useAccordionButton,
	Form,
	Card,
	Button,
	Badge,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Icon from '../../Icon/Icon';
import useLeadsStore from 'src/store/LeadsStore';
import SpinnerButton from 'src/components/SpinnerButton/SpinnerButton';
import Select from 'react-select';
import { useAuth } from 'src/hooks/useAuth';
import { RoleEnum } from 'src/api';
import i18next from 'i18next';
import { useLeadFields } from '../hooks/useLeads';

const LeadFilters = ({
	headerGroups,
	isLoading,
	initialFilter,
	onFiltersChange,
	onResetFilter,
	activeFiltersCount,
}) => {
	const { userRole } = useAuth();
	const expandMenu = useAccordionButton('0');
	const { activeEventKey } = useContext(AccordionContext);
	const { t } = useTranslation();
	const { setCheckedLeads, salesUsers, currentUserIsRefDealer } =
		useLeadsStore();
	const [filterState, setFilterState] = useState(initialFilter);

	const { leadSourceSelectOptions } = useLeadFields();

	const userSalesOptions = useMemo(() => {
		return salesUsers.map((item) => ({
			value: item.username,
			label: item.username,
		}));
	}, [salesUsers]);

	const canFilterByUserSales = useMemo(() => {
		const salesRoles = [
			RoleEnum.SALES,
			RoleEnum.SALES_DEALER,
			RoleEnum.SALES_INTERNAL,
		];

		return (
			!salesRoles.includes(userRole) ||
			(salesRoles.includes(userRole) && currentUserIsRefDealer)
		);
	}, [currentUserIsRefDealer, userRole]);

	const selectedUserSales = useMemo(() => {
		return (userSalesOptions || []).filter((item) =>
			(filterState.salesUsers || []).includes(item.value)
		);
	}, [filterState.salesUsers, userSalesOptions]);

	const selectedLeadSource = useMemo(() => {
		return (leadSourceSelectOptions || []).find(
			(item) => item.value === filterState.LeadSource
		);
	}, [filterState.LeadSource, leadSourceSelectOptions]);

	const resetFilter = () => {
		setCheckedLeads([]);
		setFilterState({
			leadIds: [],
			FirstName: '',
			LastName: '',
			Email: '',
			Phone: '',
			Codice_Fiscale__c: '',
			Partita_Iva__c: '',
			Company: '',
			CreatedDate: { from: '', to: '' },
			City: '',
			salesUser: '',
			LeadSource: null,
			Marca__c: '',
			Modello__c: '',
			Filiale_Dealer_Titolare__c: '',
		});
		onResetFilter?.();
	};

	const hasSecondaryFilter = useMemo(() => {
		return headerGroups.some((headerGroup) =>
			headerGroup.headers.some((column) => column.SecondaryFilter)
		);
	}, [headerGroups]);

	const activeFiltersLabel = useMemo(() => {
		const label =
			activeFiltersCount > 1
				? i18next.t('active_filters_count_plural')
				: i18next.t('active_filters_count');

		return `${activeFiltersCount} ${label}`;
	}, [activeFiltersCount]);

	return (
		<Form
			onSubmit={(e) => {
				e.preventDefault();
				onFiltersChange?.(filterState);
			}}
		>
			<Card className="filters-card">
				<Card.Header className="border-0">
					{activeFiltersCount > 0 ? (
						<Row className="text-end">
							<Col lg={12} className="text-end">
								<Badge
									pill
									bg="warning text-black"
									onClick={(event) => expandMenu(event)}
									style={{ cursor: 'pointer' }}
								>
									{activeFiltersLabel}
								</Badge>
							</Col>
						</Row>
					) : null}
					<Row>
						{headerGroups.map((headerGroup) =>
							headerGroup.headers.map((column) => {
								return column.PrimaryFilter ? (
									<Col key={column.id} lg={6}>
										<Form.Group className="mb-3">
											<div key={column.id}>
												<Form.Label htmlFor={column.id}>
													{column.render('Header')}
												</Form.Label>
												{column.render('PrimaryFilter', {
													props: {
														filterState,
														setFilterState,
														type: column.inputType,
													},
												})}
											</div>
										</Form.Group>
									</Col>
								) : null;
							})
						)}
					</Row>
				</Card.Header>
				<Card.Body className="py-0">
					<Accordion.Collapse eventKey="0">
						<>
							{hasSecondaryFilter && (
								<div className="my-4 pb-1 h6 border-bottom">
									{t('added_filters')}
								</div>
							)}
							<Row>
								{headerGroups.map((headerGroup) =>
									headerGroup.headers.map((column) =>
										column.SecondaryFilter ? (
											<Col key={column.id} lg={6}>
												<Form.Group className="mb-3">
													<div key={column.id}>
														<Form.Label htmlFor={column.id}>
															{column.render('Header')}
														</Form.Label>
														{column.render('SecondaryFilter', {
															props: {
																filterState,
																setFilterState,
																type: column.inputType,
															},
														})}
													</div>
												</Form.Group>
											</Col>
										) : null
									)
								)}
								<Col lg={6}>
									<Form.Group className="mb-3">
										<div>
											<Form.Label htmlFor="LeadSource">
												{t('source')}
											</Form.Label>
											<Select
												menuPosition="fixed"
												value={selectedLeadSource ? selectedLeadSource : null}
												name="LeadSource"
												isClearable
												options={leadSourceSelectOptions}
												onChange={(item) => {
													setFilterState((prev) => ({
														...prev,
														LeadSource: item?.value || null,
													}));
												}}
											/>
										</div>
									</Form.Group>
								</Col>

								{canFilterByUserSales ? (
									<Col lg={6}>
										<Form.Group className="mb-3">
											<div>
												<Form.Label htmlFor="sales_user">
													{t('sales_user')}
												</Form.Label>
												<Form.Control
													type="text"
													placeholder={t('select_sales_user')}
													value={filterState.salesUser || ''}
													onChange={(e) => {
														setFilterState((prev) => ({
															...prev,
															salesUser: e.target.value,
														}));
													}}
												/>
											</div>
										</Form.Group>
									</Col>
								) : null}
							</Row>
						</>
					</Accordion.Collapse>
				</Card.Body>
				<Card.Footer className="pt-0 border-0">
					<Row>
						<Col lg={4}></Col>
						<Col lg={4} className="open-filtri-- text-center">
							{hasSecondaryFilter && (
								<div
									className="open-filtri-container"
									onClick={(event: React.MouseEvent) => {
										event.preventDefault();
										expandMenu(event);
									}}
								>
									<div className="open-filtri-label">{t('added_filters')}</div>

									<div>
										<div>
											<Icon
												className={
													(activeEventKey === '0' ? 'is-open' : '') +
													' mt-3 expand-icon'
												}
												name="arrow-right"
											></Icon>
										</div>
									</div>
								</div>
							)}
						</Col>
						<Col lg={4} className="text-end">
							<Button
								disabled={isLoading}
								variant="secondary"
								onClick={resetFilter}
								className="me-2"
							>
								{t('reset')}
							</Button>

							<Button
								variant="primary"
								type="submit"
								onClick={() => onFiltersChange?.(filterState)}
								className="me-2"
								disabled={isLoading}
							>
								<SpinnerButton busy={isLoading} label={t('search')} />
							</Button>
						</Col>
					</Row>
				</Card.Footer>
			</Card>
		</Form>
	);
};

export default LeadFilters;
